import { useCallback, useEffect, useMemo, useState } from 'react';

import { getStorage, removeStorage, setStorage } from './utils';

// ----------------------------------------------------------------------

/**
 * Custom hook to manage state with local storage.
 *
 * @example Const { state, resetState, setState, setField, loaded } = useSecureStorage('settings', initialState);
 *
 * Return ( <div> {loaded ? ( <> <p>State: {JSON.stringify(state)}</p> <button onClick={() => setState({name: 'John',
 * age: 20})}>Set State</button> <button onClick={() => setField('name', 'John')}>Set Name</button> <button
 * onClick={resetState}>Reset</button> </> ) : ( <p>Loading...</p> )}</div> );
 *
 * @param {string} key - The key for the local storage.
 * @param {T} initialState - The initial state value.
 * @param {Object} [options] - Optional settings.
 * @param {boolean} [options.initializeWithValue=true] - Whether to initialize the local storage with the initial state
 *   value. Default is `true`
 * @returns {UseSecureStorageReturn<T>} - An object containing:
 *
 *   - `state`: The current state.
 *   - `loaded`: Boolean indicating if the state has been loaded from storage.
 *   - `resetState`: A function to reset the state to the initial value and remove it from local storage.
 *   - `setState`: A function to update the state and save it to local storage.
 *   - `setField`: A function to update a specific field in the state and save it to local storage.
 */

export type UseSecureStorageOptions = {
  initializeWithValue?: boolean;
};

export type UseSecureStorageReturn<T> = {
  state: T;
  loaded: boolean;
  resetState: (defaultState?: T) => void;
  setState: (updateState: T | Partial<T>) => void;
  setField: <K extends keyof NonNullable<T>>(name: K, updateValue: NonNullable<T>[K]) => void;
};

export function useSecureStorage<T>(
  key: string,
  initialState?: T,
  options?: UseSecureStorageOptions
): UseSecureStorageReturn<T> {
  const { initializeWithValue = true } = options ?? {};
  const isObjectState = initialState && typeof initialState === 'object';

  const [state, setState] = useState<T | undefined>(initialState);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const storedValue = getStorage<T>(key);

    if (storedValue) {
      if (isObjectState) {
        setState((prevState) => ({ ...prevState, ...storedValue }));
      } else {
        setState(storedValue);
      }
    } else if (initialState && initializeWithValue) {
      setStorage(key, initialState);
    }

    // Mark as loaded after we've checked storage and updated state
    setLoaded(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateState = useCallback(
    (newState: T | Partial<T>) => {
      if (isObjectState) {
        setState((prevValue) => {
          const updatedState = { ...prevValue, ...newState } as T;
          setStorage<T>(key, updatedState);
          return updatedState;
        });
      } else {
        setStorage<T>(key, newState as T);
        setState(newState as T);
      }
    },
    [key, isObjectState]
  );

  const updateField = useCallback(
    <K extends keyof NonNullable<T>>(fieldName: K, updateValue: NonNullable<T>[K]) => {
      if (isObjectState) {
        updateState({ [fieldName]: updateValue } as unknown as Partial<T>);
      }
    },
    [isObjectState, updateState]
  );

  const resetState = useCallback(
    (defaultState?: T) => {
      setState(defaultState ?? initialState);
      removeStorage(key);
    },
    [initialState, key]
  );

  const memoizedValue = useMemo(
    () => ({
      state: state as T,
      loaded,
      setState: updateState,
      setField: updateField,
      resetState
    }),
    [resetState, updateField, updateState, state, loaded]
  );

  return memoizedValue;
}
